import React from "react";
import { Helmet } from "react-helmet-async";
import { useT } from "@/i18n/LanguageContext";

const SITE_URL = "https://primelogichub.com";
const DEFAULT_IMAGE = `${SITE_URL}/site-assets/opening-home.webp`;

/**
 * Seo — page-level meta + JSON-LD.
 *
 * @param {string} title            Page title (site name appended automatically).
 * @param {string} description      Meta description (~155 chars).
 * @param {string} path             Route path, e.g. "/services". Used for canonical & OG URL.
 * @param {string} [image]          Absolute OG image URL.
 * @param {"website"|"article"} [type="website"]
 * @param {object|object[]} [jsonLd] JSON-LD payload(s) to inject.
 * @param {boolean} [noIndex=false] If true, emits `noindex, nofollow`.
 */
export default function Seo({
  title,
  description,
  path = "/",
  image = DEFAULT_IMAGE,
  type = "website",
  jsonLd,
  noIndex = false,
}) {
  const { lang, t } = useT();
  const canonical = `${SITE_URL}${path === "/" ? "" : path}`;
  const translatedTitle = title ? t(title) : "";
  const translatedDesc = description ? t(description) : "";
  const fullTitle = translatedTitle
    ? `${translatedTitle} | Smiles of Loudoun`
    : "Smiles of Loudoun | Dentists in Leesburg, VA";
  const ldArray = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  return (
    <Helmet prioritizeSeoTags>
      <html lang={lang === "es" ? "es" : "en"} />
      <title>{fullTitle}</title>
      <meta
        name="description"
        content={translatedDesc}
      />
      <meta
        name="robots"
        content={noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large"}
      />
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="en" href={canonical} />
      <link rel="alternate" hrefLang="es" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Smiles of Loudoun" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={translatedDesc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={lang === "es" ? "es_ES" : "en_US"} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={translatedDesc} />
      <meta name="twitter:image" content={image} />

      {ldArray.map((data, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
}

export { SITE_URL, DEFAULT_IMAGE };
